// API Response Types
export class ApiError extends Error {
  public status: number;
  public details?: Record<string, unknown> | undefined;

  constructor(status: number, message: string, details?: Record<string, unknown> | undefined) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    page_size: number;
    total?: number; // Optional - not available for audit logs
    filters?: Record<string, any>;
  };
}

// User Management Types
export interface UserProfile {
  id: string;
  name: string;
  email: string;
  user_type: 'platform_user' | 'org_user';
  enabled: boolean;
  created_at: string;
  last_login?: string;
  platform_roles?: PlatformRole[];
  organization_roles?: OrganizationRole[];
  roles?: string[]; // Unified roles field from JWT
  organization_slug?: string; // For org users - their organization
  permissions?: string[]; // User permissions from JWT
}

export interface UserPermissions {
  user_id: string;
  user_type: 'platform_user' | 'org_user';
  organizations: string[];
  global_permissions: string[];
  organization_permissions: Record<string, string[]>;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  user_type: 'platform_user' | 'org_user';
  platform_roles?: PlatformRole[];
  organization_roles?: OrganizationRole[];
  send_invitation: boolean;
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  platform_roles?: PlatformRole[];
  organization_roles?: OrganizationRole[];
  enabled?: boolean;
}

export type PlatformRole = 'admin' | 'super_admin' | 'support' | 'analytics';
export type OrganizationRole = 'owner' | 'admin' | 'event_manager' | 'sales_manager' | 'support';

// Organization Management Types
export interface Organization {
  id: string;
  slug: string;
  name: string;
  email: string;
  enabled: boolean;
  created_at: string;
  subscription_tier: 'starter' | 'professional' | 'enterprise';
  features: FeatureFlags;
  branding: OrganizationBranding;
  settings: OrganizationSettings;
}

export interface OrganizationBranding {
  logo_url?: string;
  primary_color: string;
  secondary_color: string;
  custom_domain?: string;
  favicon_url?: string;
}

export interface OrganizationSettings {
  currency: string;
  timezone: string;
  date_format: string;
  payment_methods: PaymentMethod[];
  tax_settings: TaxSettings;
  notification_preferences: NotificationSettings;
  /** Module bật/tắt (ticket 120); vắng = bật hết. */
  modules?: Record<string, { enabled?: boolean }>;
}

export interface TaxSettings {
  enabled: boolean;
  default_rate: number;
  tax_included_in_prices: boolean;
  tax_calculation_method: 'item_level' | 'order_level';
}

export interface NotificationSettings {
  email_notifications: boolean;
  sms_notifications: boolean;
  webhook_notifications: boolean;
  notification_email?: string;
}

export type PaymentMethod = 'card' | 'bank_transfer' | 'gift_card' | 'voucher' | 'paypal';

// Event Management Types
export interface Event {
  id: string;
  slug: string;
  name: string;
  description: string;
  organization_slug: string;
  status: 'draft' | 'published' | 'live' | 'ended' | 'cancelled';
  event_start: string;
  event_end: string;
  location: EventLocation;
  currency: string;
  is_public: boolean;
  meta_data: EventMetaData;
  settings: EventSettings;
  created_at: string;
  updated_at: string;
}

export interface EventLocation {
  name: string;
  address: string;
  city: string;
  country: string;
  postal_code?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

export interface EventMetaData {
  capacity?: number;
  min_age?: number;
  max_age?: number;
  dress_code?: string;
  accessibility_info?: string;
}

export interface EventSettings {
  registration_enabled: boolean;
  waiting_list_enabled: boolean;
  max_tickets_per_order: number;
  refund_policy: string;
  terms_and_conditions?: string;
}

export interface CreateEventRequest {
  name: string;
  description: string;
  event_start: string;
  event_end: string;
  location: EventLocation;
  currency: string;
  is_public: boolean;
  settings: EventSettings;
}

export interface UpdateEventRequest {
  name?: string;
  description?: string;
  event_start?: string;
  event_end?: string;
  location?: EventLocation;
  is_public?: boolean;
  settings?: EventSettings;
}

// Ticket and Item Management Types
export interface TicketItem {
  id: string;
  event_id: string;
  name: string;
  description: string;
  price: number; // in cents
  currency: string;
  category: string;
  position: number;
  active: boolean;
  available_from?: string;
  available_until?: string;
  min_per_order: number;
  max_per_order: number;
  variations: TicketVariation[];
  quotas: ItemQuota[];
  created_at: string;
  updated_at: string;
}

export interface TicketVariation {
  id: string;
  name: string;
  price: number; // in cents
  description?: string;
  active: boolean;
  position: number;
}

export interface ItemQuota {
  id: string;
  name: string;
  size: number | null; // null for unlimited
  available: number;
  blocked: number;
  pending: number;
  items: string[]; // item IDs
  subevent?: string;
}

// Order Management Types (following OpenAPI spec)
export interface Order {
  code: string;
  status: 'pending' | 'paid' | 'expired' | 'cancelled' | 'refunded';
  testmode: boolean;
  secret: string;
  email: string;
  phone?: string;
  locale: string;
  datetime: string;
  expires?: string;
  last_modified: string;
  total: string; // amount as string (following OpenAPI spec)
  positions: OrderPosition[];
  fees: any[];
  payments: any[];
  invoice_address: Record<string, any>;
  meta_data: Record<string, any>;
  comment?: string;
  checkin_attention: boolean;
  require_approval: boolean;
  sales_channel: string;
}

export interface OrderPosition {
  id: string;
  item: string;
  variation?: string;
  price: string;
  attendee_name?: string;
  attendee_email?: string;
  positionid: number;
  canceled: boolean;
  valid_from?: string;
  valid_until?: string;
  blocked?: string[];
  checkins: any[];
  downloads: any[];
  answers: any[];
  addon_to?: string;
  subevent?: string;
}

// Orders List Response (following standard structure)
export interface OrdersListResponse {
  data: Order[];
  meta: {
    page: number;
    page_size: number;
    total?: number; // Only included when withCount=1
    filters?: {
      search?: string;
      status?: string;
      email?: string;
      testmode?: boolean;
    };
  };
}

// Orders query filters (following standard structure)
export interface OrdersQueryParams {
  page?: number;
  page_size?: number;
  withCount?: 0 | 1;
  search?: string;
  status?: Order['status'];
  email?: string;
  testmode?: boolean;
}

export interface OrderItem {
  id: string;
  item_id: string;
  variation_id?: string;
  quantity: number;
  price: number; // in cents
  total_price: number; // in cents
  item_name: string;
  variation_name?: string;
}

export interface CustomerInfo {
  id?: string;
  email: string;
  name: string;
  phone?: string;
  address?: CustomerAddress;
}

export interface CustomerAddress {
  street: string;
  city: string;
  postal_code: string;
  country: string;
}

export interface PaymentInfo {
  method: 'card' | 'bank_transfer' | 'gift_card' | 'voucher';
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  amount: number; // in cents
  fee: number; // in cents
  gateway: string;
  transaction_id?: string;
  processed_at?: string;
}

// Gift Card Management Types
export interface GiftCard {
  id: string;
  code: string;
  original_value: number; // in cents
  current_balance: number; // in cents
  status: 'active' | 'redeemed' | 'expired' | 'cancelled';
  recipient_email?: string;
  sender_name?: string;
  message?: string;
  created_at: string;
  expires_at?: string;
  last_used_at?: string;
  physical_delivery: boolean;
}

export interface CreateGiftCardRequest {
  value: number; // in cents
  recipient_email: string;
  sender_name?: string;
  message?: string;
  scheduled_delivery?: string;
  physical_delivery?: boolean;
}

export interface GiftCardTransaction {
  id: string;
  type: 'purchase' | 'redemption' | 'refund' | 'expiration';
  amount: number; // in cents
  balance_after: number; // in cents
  created_at: string;
  order_id?: string;
  description?: string;
}

// Voucher Management Types
export interface Voucher {
  id: string;
  code: string;
  type: 'percentage' | 'fixed_amount';
  value: number;
  event_id: string;
  channel: string;
  campaign_name: string;
  description: string;
  valid_from: string | null;
  valid_until: string | null;
  usage_limit: number;
  usage_count: number;
  total_usage_limit: number | null;
  total_usage_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  restrictions: VoucherRestrictions;
}

export interface VoucherRestrictions {
  min_order_amount: number | null; // in cents
  max_discount_amount: number | null; // in cents
  applicable_items: string[] | null;
  first_time_customers_only: boolean;
}

export interface BulkCreateVouchersRequest {
  event_id: string;
  quantity: number;
  code_prefix: string;
  campaign_name?: string;
  description?: string;
  type: 'percentage' | 'fixed_amount';
  value: number;
  valid_from?: string;
  valid_until?: string;
  usage_limit?: number;
  total_usage_limit?: number;
  restrictions?: VoucherRestrictions;
}

export interface BulkCreateVouchersResponse {
  vouchers_created: number;
  vouchers: Voucher[];
}

export interface VoucherCampaign {
  id: string;
  name: string;
  event_id: string;
  voucher_count: number;
  total_usage: number;
  total_discount: number; // in cents
  created_at: string;
  status: 'active' | 'paused' | 'ended';
}

// Dynamic Pricing Types
export interface PricingRule {
  id: string;
  event_id: string;
  name: string;
  rule_type: 'early_bird' | 'last_minute' | 'bulk_discount' | 'member_discount' | 'custom';
  description: string;
  is_enabled: boolean;
  priority: number;
  active_from: string | null;
  active_until: string | null;
  max_applications: number | null;
  current_applications: number;
  trigger_conditions: TriggerCondition[];
  adjustment: PriceAdjustment;
  applicable_items: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface TriggerCondition {
  type: 'time' | 'sold_percentage' | 'sold_quantity' | 'customer_type';
  operator: 'gt' | 'gte' | 'lt' | 'lte' | 'eq';
  value: number | string;
}

export interface PriceAdjustment {
  type: 'percentage_discount' | 'fixed_discount' | 'percentage_markup' | 'fixed_markup' | 'absolute_price';
  value: number;
  min_price: number | null; // in dollars
  max_price: number | null; // in dollars
}

export interface PriceCalculationRequest {
  event_id: string;
  calculation_time?: string;
  items: PriceCalculationItem[];
  context?: PricingContext;
}

export interface PriceCalculationItem {
  item_id: string;
  variation_id?: string;
  quantity: number;
  base_price: number; // in cents
}

export interface PricingContext {
  customer_type?: string;
  sold_percentage?: number;
  sold_quantity?: number;
  timestamp?: string;
}

// Analytics Types
export interface EventAnalytics {
  event_id: string;
  period_start: string;
  period_end: string;
  total_sales: number; // in cents
  total_orders: number;
  conversion_rate: number;
  average_order_value: number; // in cents
  refund_rate: number;
  ticket_sales: TicketSalesBreakdown[];
  sales_timeline: SalesTimelinePoint[];
  payment_methods: PaymentMethodBreakdown[];
  customer_analytics: CustomerAnalytics;
}

export interface TicketSalesBreakdown {
  item_id: string;
  item_name: string;
  quantity_sold: number;
  revenue: number; // in cents
  percentage_of_total: number;
}

export interface SalesTimelinePoint {
  date: string;
  sales: number; // in cents
  orders: number;
  cumulative_sales: number; // in cents
}

export interface CustomerAnalytics {
  total_customers: number;
  new_customers: number;
  returning_customers: number;
  average_age: number;
  gender_breakdown: GenderBreakdown;
  location_breakdown: LocationBreakdown[];
}

export interface GenderBreakdown {
  male: number;
  female: number;
  other: number;
  not_specified: number;
}

export interface LocationBreakdown {
  country: string;
  customers: number;
  percentage: number;
}

export interface RevenueAnalytics {
  total_revenue: number; // in cents
  net_revenue: number; // in cents
  refunds: number; // in cents
  fees: number; // in cents
  currency: string;
  breakdown_by_event: EventRevenueBreakdown[];
  breakdown_by_month: MonthlyRevenueBreakdown[];
  payment_method_breakdown: PaymentMethodBreakdown[];
}

export interface EventRevenueBreakdown {
  event_id: string;
  event_name: string;
  revenue: number; // in cents
  percentage_of_total: number;
}

export interface MonthlyRevenueBreakdown {
  month: string;
  revenue: number; // in cents
  orders: number;
  growth_rate: number;
}

export interface PaymentMethodBreakdown {
  method: PaymentMethod;
  transactions: number;
  amount: number; // in cents
  percentage: number;
}

export interface OrganizationAnalytics {
  organization_id: string;
  period_start: string;
  period_end: string;
  total_events: number;
  active_events: number;
  total_revenue: number; // in cents
  revenue_growth: number;
  revenue_timeline: SalesTimelinePoint[];
  top_events: EventRevenueBreakdown[];
}

export interface GiftCardAnalytics {
  total_issued: number;
  total_value: number; // in cents
  total_redeemed: number;
  redemption_rate: number;
  average_value: number; // in cents
  expiration_rate: number;
}

// System and Admin Types — khớp 1:1 với models.SystemStatusResponse (bo-gateway, admin.go)
export interface SystemStatus {
  status: 'healthy' | 'degraded';
  timestamp: string;
  version: string;
  environment: string;
  services: Record<string, ServiceStatus>;
  metrics: SystemMetrics;
  resources: SystemResources;
}

export interface ServiceStatus {
  status: 'up' | 'down' | 'unmonitored';
  response_time: string;
}

export interface SystemMetrics {
  active_users: number;
  total_requests_24h: number;
  average_response_time: string;
  error_rate: string;
}

export interface SystemResources {
  cpu_usage: string;
  memory_usage: string;
  disk_usage: string;
}

export interface AuditLog {
  id: string;
  resource: string;
  resource_id: string;
  action: string;
  user_id?: string;
  user_email?: string;
  user_agent?: string;
  ip_address?: string;
  organization?: string;
  details?: Record<string, unknown>;
  severity?: string;
  source?: string;
  timestamp: string;
}

// Feature Flags
export interface FeatureFlags {
  // Core features
  events_enabled: boolean;
  orders_enabled: boolean;
  payments_enabled: boolean;
  
  // Advanced features
  gift_cards_enabled: boolean;
  vouchers_enabled: boolean;
  dynamic_pricing_enabled: boolean;
  analytics_dashboard_enabled: boolean;
  
  // Integration features
  webhooks_enabled: boolean;
  api_access_enabled: boolean;
  
  // Enterprise features
  white_label_enabled: boolean;
  custom_domain_enabled: boolean;
  advanced_analytics_enabled: boolean;
  priority_support_enabled: boolean;
}

// Filter Types
export interface UserFilters {
  user_type?: 'platform_user' | 'org_user' | 'all';
  search?: string;
  enabled?: boolean;
  organization_id?: string;
  page?: number;
  page_size?: number;
}

export interface OrganizationFilters {
  search?: string;
  enabled?: boolean;
  subscription_tier?: 'starter' | 'professional' | 'enterprise';
  page?: number;
  page_size?: number;
}

export interface EventFilters {
  search?: string;
  status?: Event['status'];
  is_public?: boolean;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface OrderFilters {
  search?: string;
  status?: Order['status'];
  event_id?: string;
  customer_email?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface GiftCardFilters {
  search?: string;
  status?: GiftCard['status'];
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface VoucherFilters {
  search?: string;
  campaign_name?: string;
  is_active?: boolean;
  valid_from?: string;
  valid_until?: string;
  page?: number;
  page_size?: number;
}

// organization KHÔNG có ở đây — pbaudit.QueryAuditLogsRequest (backend) không hỗ trợ lọc theo tổ
// chức, dropdown tương ứng đã gỡ khỏi AuditLogsPage (trước đó gửi lên nhưng bo-gateway âm thầm bỏ qua).
export interface AuditLogFilters {
  user_id?: string;
  action?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
  page?: number;
  page_size?: number;
}